import { useRef, useState } from "react";
import WidgetChatComposer from "../../../../Components/WidgetChatComposer/WidgetChatComposer";
import WidgetContentList from "../../../../Components/WidgetContentList/WidgetContentList";
import WidgetMenuTag from "../../../../Components/WidgetMenuTag/WidgetMenuTag";
import WidgetPoweredBy from "../../../../Components/WidgetPoweredBy/WidgetPoweredBy";
import { useConfigStore } from "../../../../Stores/Config";
import { isConversationRoute, useRouterStore } from "../../../../Stores/Router";
import { useTicketStore } from "../../../../Stores/Ticket";
import "./AgentHomeV4.scss";
import AgentHomeV4Header from "./AgentHomeV4Header";

const AgentHomeV4 = () => {
  const configStore = useConfigStore();
  const aiTakeoffActive = useTicketStore((state) => state.aiTakeoffActive);
  const currentPage = useRouterStore((state) => state.currentPage);
  const [headerOpacity, setHeaderOpacity] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerWrapRef = useRef<HTMLDivElement>(null);

  // Mobile: lift the hero composer to the top on focus so the greeting slides up
  // out of the way and it feels native. Driven by FOCUS, not visualViewport —
  // the visualViewport keyboard signal (event AND height) is unreliable inside
  // the widget iframe on iOS, so focus is the only dependable trigger. Gated to
  // touch devices via matchMedia so a plain desktop focus never scrolls. iOS
  // overlays the keyboard (the layout viewport never shrinks), so we add fixed
  // bottom room for it, then scroll our OWN container — never scrollIntoView (it
  // crosses the iframe and scrolls the host page on Safari). Blur restores, so
  // every focus re-arms the lift (not just the first).
  // TALL tablets (iPad portrait/landscape) are excluded: the hero sits well
  // above their keyboard, so the lift only scrolled the logo bar and greeting
  // off the top for nothing. Wide-but-short viewports (an unfolded Galaxy Fold
  // in landscape, ~700px tall) still lift — their keyboard covers the hero.
  // Width breakpoint matches the tablet column in mixins.scss.
  const isTouchDevice = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches &&
    !window.matchMedia("(min-width: 700px) and (min-height: 800px)").matches;

  const liftComposerAboveKeyboard = () => {
    const scroll = scrollRef.current;
    if (!isTouchDevice() || !scroll) {
      return;
    }
    scroll.style.paddingBottom = "320px"; // room so the composer can reach the top
    window.setTimeout(() => {
      const composer = composerWrapRef.current;
      if (!scrollRef.current || !composer) {
        return;
      }
      const target =
        composer.getBoundingClientRect().top -
        scrollRef.current.getBoundingClientRect().top +
        scrollRef.current.scrollTop -
        12;
      scrollRef.current.scrollTo({
        top: Math.max(0, target),
        behavior: "smooth",
      });
    }, 300);
  };

  const restoreAfterKeyboard = () => {
    const scroll = scrollRef.current;
    // Skipped during takeoff — sending blurs the input as the composer glides
    // off and the home is unmounting anyway.
    if (
      !isTouchDevice() ||
      !scroll ||
      useTicketStore.getState().aiTakeoffActive
    ) {
      return;
    }
    scroll.style.paddingBottom = "";
    scroll.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Keep the takeoff fade applied through the whole hand-off, not just while
  // aiTakeoffActive is true. The store flips aiTakeoffActive→false the instant it
  // pushes the loading shell, but App renders the routed page from a DEFERRED
  // copy of currentPage (useDeferredValue), so THIS home stays mounted for a
  // frame or two after the nav. Dropping the fade the moment aiTakeoffActive
  // cleared snapped the hero/pills/content back to opacity 1 for those frames —
  // a full-home flash mid-takeoff. currentPage flips immediately, so gate on it
  // too: stay faded once we're already navigating into the conversation.
  const takeoffFaded = aiTakeoffActive || isConversationRoute(currentPage);

  const menuWidgets = configStore.homeWidgets.filter((w) => w.type === "menu");
  const menuAsList = configStore.config.homeMenuStyle === "list";
  const menuTitle = configStore.config.homeMenuTitle;

  return (
    <div
      className={`home-page home-page--v4 ${
        takeoffFaded ? "home-page--takeoff" : ""
      }`}
    >
      <div
        ref={scrollRef}
        className="agent-home-v4-scroll"
        onScroll={(e) => {
          // Fade the hero header out over ~250px of scroll (was 100px, which
          // vanished the background too fast on mobile).
          let opacity = 1 - e.currentTarget.scrollTop / 250;
          if (opacity < 0) opacity = 0;
          if (opacity > 1) opacity = 1;
          setHeaderOpacity(opacity);
        }}
      >
        <div
          className="agent-home-v4-hero"
          style={{ opacity: takeoffFaded ? undefined : headerOpacity }}
        >
          <AgentHomeV4Header />
        </div>

        <div className="agent-home-v4-composer" ref={composerWrapRef}>
          <WidgetChatComposer
            variant="v4"
            onFocus={liftComposerAboveKeyboard}
            onBlur={restoreAfterKeyboard}
            sendMessage={(message, opts) => {
              useTicketStore.getState().createNewConversation(
                {
                  aiQuestion: true,
                  navDelayMs: opts.navDelayMs,
                  firstMessageAttachments: opts.attachments,
                },
                message,
              );
            }}
          />
        </div>

        {menuWidgets.length > 0 && (
          <div className={`agent-home-v4-pills${menuAsList ? " agent-home-v4-pills--list" : ""}`}>
            {menuAsList ? <>
              {menuTitle && <h2 id="home-menu-title" className="agent-home-v4-menu-title">{menuTitle}</h2>}
              <div className="agent-home-v4-menu-list" role="group" aria-labelledby={menuTitle ? "home-menu-title" : undefined}>
                {menuWidgets.map((widget, index) => <WidgetMenuTag menuItem={widget.config} index={index} variant="list" key={index} />)}
              </div>
            </> : menuWidgets.map((widget, index) => (
                <WidgetMenuTag
                  menuItem={widget.config}
                  index={index}
                  variant="v4"
                  key={index}
                />
              ))}
          </div>
        )}

        <div className="widget-menu agent-home-v4-content">
          <WidgetContentList />
        </div>

        <WidgetPoweredBy />
      </div>
    </div>
  );
};

export default AgentHomeV4;
